import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { apiClient } from '@/lib/api-client';
import { useKeyedPendingWrite } from '@/lib/use-pending-write';
import { isStale, problemMessage, standingChanged } from '../api/problem';
import type { OrganizationRow } from './use-organizations';

export type Acts = ReturnType<typeof useOrganizationActs>;

/**
 * Archive and restore: each acts at once and says so in a toast. A refusal is said in plain words;
 * one that says the list is stale reads it again, and one that says the caller's standing changed
 * reads the place again too.
 */
export function useOrganizationActs(
  tenantId: string,
  reload: () => void,
  refreshPlace: () => void,
) {
  const { t } = useTranslation('administration');
  const { isPending, run } = useKeyedPendingWrite();

  const refused = useCallback(
    (error: unknown, status: number) => {
      toast.error(problemMessage(t, error, status));
      if (standingChanged(error)) {
        refreshPlace();
      }
      if (isStale(error)) {
        reload();
      }
    },
    [t, reload, refreshPlace],
  );

  const archive = useCallback(
    (row: OrganizationRow) =>
      void run(`archive:${row.id}`, async () => {
        const { error, response } = await apiClient.POST(
          '/v1/administration/tenants/{tenantId}/organizations/{organizationId}/archival',
          { params: { path: { tenantId, organizationId: row.id } }, body: {} },
        );
        if (!response.ok) {
          refused(error, response.status);
          return;
        }
        toast.success(t('organizations.toast.archived', { name: row.name }));
        reload();
      }),
    [run, tenantId, refused, t, reload],
  );

  const restore = useCallback(
    (row: OrganizationRow) =>
      void run(`restore:${row.id}`, async () => {
        const { error, response } = await apiClient.POST(
          '/v1/administration/tenants/{tenantId}/organizations/{organizationId}/restoration',
          { params: { path: { tenantId, organizationId: row.id } }, body: {} },
        );
        if (!response.ok) {
          refused(error, response.status);
          return;
        }
        toast.success(t('organizations.toast.restored', { name: row.name }));
        reload();
      }),
    [run, tenantId, refused, t, reload],
  );

  return { archive, restore, isPending };
}
